import { z } from 'zod';
import {
  COUNTRIES,
  type ClassifierAdapter,
  type CountryConfig,
  type RegionCatalog,
} from './types.js';

// Grows alongside CountryConfig. Validates CONTENT that TypeScript can't prove.
export const countryConfigSchema = z
  .object({
    code: z.enum(COUNTRIES),
    locales: z.array(z.string()).nonempty(),
    defaultLocale: z.string(),
    indexedLocales: z.array(z.string()).nonempty().optional(),
    regions: z.custom<RegionCatalog>(
      (v) => typeof (v as { localize?: unknown })?.localize === 'function',
    ),
    currency: z.object({
      code: z.string().length(3),
      symbol: z.string().min(1),
      compactUnits: z.record(z.array(z.string())),
      words: z.record(z.string().min(1)),
    }),
    brand: z.object({
      name: z.string().min(1),
      siteUrl: z.string().url(),
      supportEmail: z.string().email(),
      // Published human Telegram contact, or null for "show none". Separate from
      // `messaging.telegram` (the bot) — see BrandConfig.
      supportTelegram: z
        .object({ handle: z.string().regex(/^@\w+$/), url: z.string().url() })
        .nullable(),
      // Whether the operator's phone / WhatsApp line is published.
      publishPhone: z.boolean(),
      // The accent colour as `#rrggbb` — the web turns it into `R G B` CSS
      // variables, so nothing but six hex digits is accepted.
      palette: z.object({
        primary: z.string().regex(/^#[0-9a-fA-F]{6}$/),
        mid: z.string().regex(/^#[0-9a-fA-F]{6}$/),
        light: z.string().regex(/^#[0-9a-fA-F]{6}$/),
      }),
    }),
    // Country display name per UI locale, in nominative/genitive/prepositional cases.
    countryName: z.record(
      z.object({ nom: z.string().min(1), gen: z.string().min(1), prep: z.string().min(1) }),
    ),
    // Web ИНН/БИН form descriptor (label + digit count).
    companyIdForm: z.object({ label: z.string().min(1), digits: z.number().int().positive() }),
    phonePlaceholder: z.string().min(1),
    // Anchored, stateless: a `g`/`y` flag would make the shared pattern's
    // `.test` depend on the previous call (`lastIndex`).
    productCodePattern: z
      .instanceof(RegExp)
      .refine(
        (r) => r.source.startsWith('^') && r.source.endsWith('$') && !/[gy]/.test(r.flags),
        'productCodePattern must be anchored (^…$) and carry no g/y flag',
      ),
    // Same rule for the search-box code shape; absent = no code queries (UZ).
    searchCodePattern: z
      .instanceof(RegExp)
      .refine(
        (r) => r.source.startsWith('^') && r.source.endsWith('$') && !/[gy]/.test(r.flags),
        'searchCodePattern must be anchored (^…$) and carry no g/y flag',
      )
      .optional(),
    // IANA zone; absent = Asia/Tashkent (UZ, KZ).
    timeZone: z
      .string()
      .regex(/^[A-Za-z]+\/[A-Za-z_]+$/)
      .optional(),
    // Meta Pixel id + GA4 measurement id baked into index.html per country. GA4
    // id may be '' (disables GA4 for the deployment), so no .min(1) on it.
    analytics: z.object({ metaPixelId: z.string().min(1), ga4MeasurementId: z.string() }),
    // Which messaging channels the deployment offers. `telegram` gates the web's
    // Telegram surfaces and the prod build's VITE_TELEGRAM_BOT_USERNAME requirement.
    // `telegramBot` is the bare username (no @), `telegramChannel` the advertised
    // channel or null. A bot username without the bot switched on (or the reverse)
    // is a half-wired deployment and is refused below.
    messaging: z.object({
      telegram: z.boolean(),
      telegramBot: z
        .string()
        .regex(/^[A-Za-z]\w{3,30}bot$/i)
        .nullable(),
      telegramChannel: z
        .object({ handle: z.string().regex(/^@\w+$/), url: z.string().url() })
        .nullable(),
      emailFirstAlert: z.boolean(),
      // Optional: absent = the create-time first lot reads the request (UZ); KZ sets it.
      singleFirstAlert: z.boolean().optional(),
    }),
    // Analytics/UX surfaces gated per deployment. `priceIntel` gates /market,
    // /prices and the kind filter (all built on the ИКПУ taxonomy + price history).
    features: z.object({
      priceIntel: z.boolean(),
      teamPlans: z.boolean(),
      corporateContactGate: z.boolean(),
      conversionLandings: z.boolean(),
      aiOnboarding: z.boolean(),
      honestTeasers: z.boolean(),
      supplyScope: z.boolean(),
      // Optional: absent = a refused login code keeps its digits in the boxes (UZ/KZ).
      refusedCodeCleared: z.boolean().optional(),
    }),
    // The `ifi` sources whose chips /ifi may draw — checked against
    // `sourceKeys` in the refine below.
    ifiSources: z.array(z.string().min(1)),
    // The non-ifi platforms whose chips /catalog may draw — checked against
    // `sourceKeys` (and kept apart from `ifiSources`) in the refines below.
    govSources: z.array(z.string().min(1)),
    // How the deployment takes money: the checkout shape, which plan families
    // are sold, the marks by the pay button, and who accepts the card.
    payments: z.object({
      checkout: z.enum(['local', 'stripe']),
      yearlyPlans: z.boolean(),
      bankInvoice: z.boolean(),
      ipPlan: z.boolean(),
      marks: z
        .array(
          z.enum(['payme', 'click', 'octobank', 'uzcard', 'humo', 'visa', 'mastercard', 'stripe']),
        )
        .nonempty(),
      cardPayee: z
        .object({
          name: z.string().min(1),
          address: z.string().min(1),
          phone: z.string().min(1),
          processor: z.string().min(1),
        })
        .nullable(),
      // USD invoice on a Stripe deployment (KG); absent everywhere else.
      invoiceUsd: z
        .object({
          kgsPerUsd: z.number().positive(),
          rateSource: z.string().min(1),
          amountsUsd: z.record(z.number().int().positive()),
        })
        .optional(),
    }),
    // Empty allowed: a not-yet-launched country (e.g. kz before its adapters exist)
    // legitimately ingests nothing. A misconfigured live country surfaces as "0 sources
    // enabled" in ingestion health, not a boot crash.
    sourceKeys: z.array(z.string()),
    // Direct contracts kept out of the concentration reads. Markers are matched
    // as lower-case substrings, so an upper-case or a too-short one would
    // silently match nothing (or nearly everything) — refused here.
    competition: z.object({
      nonCompetitiveSources: z.array(z.string().min(1)),
      nonCompetitiveMethodMarkers: z.array(
        z
          .string()
          .min(5)
          .refine((m) => m === m.toLowerCase(), 'method markers must be lower-case'),
      ),
      unknownMethodIsCompetitive: z.boolean(),
    }),
    companyId: z.object({
      label: z.string().min(1),
      // Deliberately NOT a content smoke test (e.g. "accepts a seeded 9-digit
      // positive"): company-id shape is country-specific (UZ ИНН is 9 digits;
      // a future country's scheme may not be), and this schema is shared
      // across all countries — a fixture shaped for one would wrongly fail
      // another's valid config. `typeof === 'function'` is the same content
      // ceiling `regions.localize` above already accepts for the same reason.
      validate: z.custom<(id: string) => boolean>((v) => typeof v === 'function'),
    }),
    // Same `typeof === 'function'` content ceiling as `regions`/`companyId`
    // above: an object exposing both `resolve` and `classify` functions. The
    // rule tables are country-specific, so a behavioural fixture here would
    // wrongly fail another country's classifier.
    classifier: z.custom<ClassifierAdapter>(
      (v) =>
        typeof (v as { label?: unknown })?.label === 'string' &&
        ['level2', 'ensTruClass'].includes(
          (v as { vocabularyBucket?: unknown })?.vocabularyBucket as string,
        ) &&
        typeof (v as { resolve?: unknown })?.resolve === 'function' &&
        typeof (v as { classify?: unknown })?.classify === 'function',
    ),
    companyIndex: z.object({
      minCompetitiveAwards: z.number().int().positive(),
      windowMonths: z.number().int().positive(),
      requireActiveStatus: z.boolean(),
      indexCap: z.number().int().positive().optional(),
    }),
    // Credible award-price band in local currency (the plausibility sweep).
    awardPlausibility: z
      .object({ floor: z.number().positive(), ceiling: z.number().positive() })
      .refine((p) => p.floor < p.ceiling, 'awardPlausibility.floor must be below ceiling'),
    // Money thresholds in local currency (ingestion budget floor, bid-package
    // complex tier, stuck-checkout band).
    procurementKindFromSource: z.boolean(),
    // Dot-segment (UZ, KZ) or plain-prefix (KG ОКГЗ) code hierarchy.
    productCodePrefix: z.enum(['dotted', 'flat']),
    thresholds: z.object({
      minPlausibleBudget: z.number().positive(),
      complexLotBudget: z.number().int().positive(),
      stuckCheckoutBand: z.number().positive(),
    }),
  })
  .refine((c) => c.locales.includes(c.defaultLocale), 'defaultLocale must be one of locales')
  .refine(
    (c) => (c.indexedLocales ?? c.locales).every((l) => c.locales.includes(l)),
    'every indexedLocales entry must be one of locales',
  )
  .refine(
    (c) => c.messaging.telegram === (c.messaging.telegramBot !== null),
    'messaging.telegramBot must be set exactly when messaging.telegram is on',
  )
  .refine(
    (c) => c.ifiSources.every((k) => c.sourceKeys.includes(k)),
    'every ifiSources key must be ingested (listed in sourceKeys)',
  )
  .refine(
    (c) => c.govSources.every((k) => c.sourceKeys.includes(k)),
    'every govSources key must be ingested (listed in sourceKeys)',
  )
  .refine(
    (c) => c.govSources.every((k) => !c.ifiSources.includes(k)),
    'a source is either an /ifi chip or a /catalog chip, never both',
  );

export function assertCountryConfig(c: CountryConfig): void {
  const r = countryConfigSchema.safeParse(c);
  if (!r.success) throw new Error(`Invalid CountryConfig '${String(c.code)}': ${r.error.message}`);
}
